var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Button, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
export var FLOATING_SHADOW = '0 4px 16px rgba(15, 23, 42, 0.08)';
// Only one edit overlay can be open within a sidebar.
var EditingCardContext = createContext({ setEditing: function () { return undefined; } });
export var SidebarCardsProvider = function (_a) {
    var children = _a.children;
    var _b = useState(), editing = _b[0], setEditing = _b[1];
    return _jsx(EditingCardContext.Provider, __assign({ value: { editing: editing, setEditing: setEditing } }, { children: children }));
};
export var AddButton = function (_a) {
    var onClick = _a.onClick;
    var t = useTranslation().t;
    return (_jsx(Button, __assign({ size: 'small', startIcon: _jsx(AddIcon, { sx: { fontSize: '16px !important' } }), onClick: function (event) { return onClick(event.currentTarget); }, sx: { height: 28, minWidth: 0, px: 1, fontSize: 14, fontWeight: 600, textTransform: 'none' } }, { children: t('Add') })));
};
export var CardActionButton = function (_a) {
    var icon = _a.icon, onClick = _a.onClick, disabled = _a.disabled, children = _a.children;
    return (_jsx(Button, __assign({ variant: 'text', size: 'small', startIcon: icon, onClick: onClick, disabled: disabled, sx: {
            height: 28,
            my: '-2px',
            mr: '-6px',
            minWidth: 0,
            px: '10px',
            borderRadius: '6px',
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            flexShrink: 0,
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': { mr: '4px' },
            '& .MuiButton-startIcon svg': { fontSize: 14 },
            '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.04)' }
        } }, { children: children })));
};
var EditIconButton = function (_a) {
    var onClick = _a.onClick, disabled = _a.disabled;
    var t = useTranslation().t;
    return (_jsx(Tooltip, __assign({ title: t('Edit'), arrow: true }, { children: _jsx(Box, __assign({ component: 'span', sx: { display: 'inline-flex', flexShrink: 0 } }, { children: _jsx(IconButton, __assign({ "aria-label": t('Edit'), onClick: onClick, disabled: disabled, sx: {
                    width: 28,
                    height: 28,
                    my: '-2px',
                    top: '-1px',
                    borderRadius: '6px',
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.04)', color: 'primary.main' }
                } }, { children: _jsx(EditOutlinedIcon, { sx: { fontSize: 16 } }) })) })) })));
};
export var CardTitle = function (_a) {
    var title = _a.title, afterTitle = _a.afterTitle, action = _a.action;
    return (_jsxs(Stack, __assign({ direction: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px', minHeight: 24 }, { children: [_jsxs(Stack, __assign({ direction: 'row', alignItems: 'center', gap: '2px', sx: { minWidth: 0 } }, { children: [_jsx(Typography, __assign({ noWrap: true, sx: { minWidth: 0, fontSize: 13, fontWeight: 600, letterSpacing: '0.02em' }, color: 'text.secondary' }, { children: title })), afterTitle] })), action] })));
};
export var SidebarCard = function (_a) {
    var title = _a.title, onEdit = _a.onEdit, editContent = _a.editContent, saving = _a.saving, onCancel = _a.onCancel, onSave = _a.onSave, children = _a.children;
    var t = useTranslation().t;
    var _b = useContext(EditingCardContext), editing = _b.editing, setEditing = _b.setEditing;
    var open = !!editContent;
    useEffect(function () {
        if (!open)
            return;
        setEditing(title);
        return function () { return setEditing(function (current) { return current === title ? undefined : current; }); };
    }, [open, title, setEditing]);
    return (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsxs(Box, __assign({ sx: { position: 'relative' } }, { children: [_jsxs(Stack, __assign({ gap: '10px' }, { children: [_jsx(CardTitle, { title: title, afterTitle: onEdit && _jsx(EditIconButton, { onClick: onEdit, disabled: !!editing && editing !== title }) }), children] })), editContent && (_jsxs(Stack, __assign({ gap: '10px', sx: {
                            position: 'absolute',
                            top: '-12px',
                            left: '-12px',
                            right: '-12px',
                            zIndex: 10,
                            bgcolor: 'background.paper',
                            border: '2px solid',
                            borderColor: 'primary.main',
                            borderRadius: '12px',
                            padding: '10px',
                            boxShadow: FLOATING_SHADOW
                        } }, { children: [_jsx(CardTitle, { title: title }), editContent, _jsxs(Stack, __assign({ direction: 'row', justifyContent: 'flex-end', gap: 1, pt: '6px' }, { children: [_jsx(Button, __assign({ onClick: onCancel, disabled: saving, sx: { textTransform: 'none', color: 'text.secondary' } }, { children: t('Cancel') })), _jsx(LoadingButton, __assign({ variant: 'contained', onClick: onSave, loading: saving, sx: { textTransform: 'none' } }, { children: t('Save') }))] }))] })))] }))] }));
};
/** One label + value row; rows sit in a FieldRows grid so all labels share one column. */
export var FieldRow = function (_a) {
    var label = _a.label, alignTop = _a.alignTop, children = _a.children;
    return (_jsxs(_Fragment, { children: [_jsx(Typography, __assign({ sx: { fontSize: 14, fontWeight: 500, alignSelf: alignTop ? 'start' : 'center', pt: alignTop ? '6px' : 0 }, color: 'text.secondary' }, { children: label })), _jsx(Box, __assign({ sx: { minHeight: 30, minWidth: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', fontSize: 15 } }, { children: children }))] }));
};
export var FieldRows = function (_a) {
    var children = _a.children;
    return (_jsx(Box, __assign({ sx: { display: 'grid', gridTemplateColumns: '84px minmax(0, 1fr)', columnGap: '12px', rowGap: '8px', alignItems: 'center' } }, { children: children })));
};
